/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  buildEvolucaoStackedSeries,
  EVOLUCAO_SERIES_KEYS,
  evolucaoHasData,
  mapEvolucaoToDataset,
  TIPO_NOTA_BY_KPI_KEY,
} from './constants';
import {
  ChartEmpty,
  CHART_HEIGHT,
  LocalFilterChip,
  toggleLocalCategoria,
} from './chartShared';

const EvolucaoChart = ({ items, loading, height }) => {
  const [localCategoriaKey, setLocalCategoriaKey] = useState(null);
  const dataset = useMemo(() => mapEvolucaoToDataset(items), [items]);
  const allSeries = useMemo(() => buildEvolucaoStackedSeries(), []);

  const series = useMemo(() => {
    if (!localCategoriaKey) return allSeries;
    return allSeries.filter((s) => s.id === localCategoriaKey || s.dataKey === localCategoriaKey);
  }, [allSeries, localCategoriaKey]);

  const hasData = evolucaoHasData(dataset);
  const chartHeight = height || CHART_HEIGHT;

  const localLabel = localCategoriaKey
    ? (TIPO_NOTA_BY_KPI_KEY[localCategoriaKey]?.label || localCategoriaKey)
    : null;

  const toggleBySeriesId = useCallback((seriesId) => {
    if (!seriesId) return;
    const key = EVOLUCAO_SERIES_KEYS.some((s) => s.key === seriesId)
      ? seriesId
      : null;
    if (!key) return;
    setLocalCategoriaKey((prev) => toggleLocalCategoria(prev, key));
  }, []);

  const handleItemClick = useCallback(
    (_event, identifier) => {
      toggleBySeriesId(identifier?.seriesId);
    },
    [toggleBySeriesId],
  );

  const handleLegendClick = useCallback(
    (_event, legendItem) => {
      toggleBySeriesId(legendItem?.seriesId);
    },
    [toggleBySeriesId],
  );

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  if (!hasData) {
    return <ChartEmpty message="Sem gastos no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }} aria-label="Gráfico de evolução mensal de gastos">
      <LocalFilterChip
        label={localLabel ? `Categoria: ${localLabel}` : null}
        onClear={() => setLocalCategoriaKey(null)}
      />
      <BarChart
        dataset={dataset}
        xAxis={[{ scaleType: 'band', dataKey: 'mesLabel' }]}
        series={series}
        height={chartHeight}
        margin={{ left: 56, right: 16, top: 16, bottom: 48 }}
        onItemClick={handleItemClick}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: 0,
            onItemClick: handleLegendClick,
          },
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiChartsLegend-root button': { cursor: 'pointer' },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Clique em uma série ou legenda para filtrar só neste gráfico
      </Typography>
    </Box>
  );
};

EvolucaoChart.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  height: PropTypes.number,
};

EvolucaoChart.defaultProps = {
  items: [],
  loading: false,
  height: null,
};

export default EvolucaoChart;
